import { createPortal } from 'react-dom';
import { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import type { CrmContact } from '../../types/crm';
import { buildApiUrl } from '../../services/api';
import { validatePhone } from '../../utils/phoneUtils';
import { applyWhatsAppTemplateVariables, buildWhatsAppDeepLink } from '../../utils/whatsappTemplate';

interface CrmWhatsAppTemplatePopoverProps {
  isOpen: boolean;
  contact: CrmContact;
  stageKey: string;
  stageLabel: string;
  companyName?: string;
  anchorRect: DOMRect;
  onClose: () => void;
}

interface CrmWhatsappTemplate {
  id: string;
  name: string;
  content: string;
  description?: string | null;
  active: boolean;
  kanbanStages?: string[];
  sortOrder?: number;
}

const registerTemplateUsage = async (templateId: string, channel: 'WHATSAPP') => {
  const token = localStorage.getItem('auth_token');
  const headers: HeadersInit = {
    'Content-Type': 'application/json'
  };

  if (token) {
    (headers as Record<string, string>).Authorization = `Bearer ${token}`;
  }

  await fetch(buildApiUrl(`/templates/${templateId}/use`), {
    method: 'POST',
    headers,
    body: JSON.stringify({ channel })
  });
};

const fetchTemplatesByStage = async (stageKey: string): Promise<CrmWhatsappTemplate[]> => {
  const token = localStorage.getItem('auth_token');
  const headers: HeadersInit = {};

  if (token) {
    (headers as Record<string, string>).Authorization = `Bearer ${token}`;
  }

  const params = new URLSearchParams({
    messageType: 'TEXT',
    active: 'true',
    kanbanStage: stageKey
  });

  const response = await fetch(buildApiUrl(`/templates?${params.toString()}`), {
    headers
  });

  if (!response.ok) {
    const errorPayload = await response.json().catch(() => ({}));
    throw new Error(errorPayload.error || `HTTP ${response.status}`);
  }

  const payload = await response.json();
  return (payload.data || []) as CrmWhatsappTemplate[];
};

export function CrmWhatsAppTemplatePopover({
  isOpen,
  contact,
  stageKey,
  stageLabel,
  companyName,
  anchorRect,
  onClose
}: CrmWhatsAppTemplatePopoverProps) {
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const [templates, setTemplates] = useState<CrmWhatsappTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [draftMessage, setDraftMessage] = useState('');

  const selectedTemplate = useMemo(
    () => templates.find((template) => template.id === selectedTemplateId) || null,
    [selectedTemplateId, templates]
  );

  const popoverPosition = useMemo(() => {
    if (typeof window === 'undefined') {
      return {
        left: 12,
        top: 12
      };
    }

    const width = 360;
    const viewportPadding = 12;
    const left = Math.min(
      Math.max(viewportPadding, anchorRect.right - width),
      Math.max(viewportPadding, window.innerWidth - width - viewportPadding)
    );
    const estimatedHeight = 520;
    const belowTop = anchorRect.bottom + 12;
    const aboveTop = Math.max(viewportPadding, anchorRect.top - estimatedHeight - 12);
    const top = belowTop + estimatedHeight > window.innerHeight ? aboveTop : belowTop;

    return {
      left,
      top
    };
  }, [anchorRect]);

  const templateContext = useMemo(() => ({
    nome: contact.nome,
    empresa: companyName || contact.categoria?.nome || '',
    telefone: contact.telefone,
    email: contact.email || '',
    responsavel: contact.responsavel || '',
    etapa_funil: stageLabel
  }), [companyName, contact.categoria?.nome, contact.email, contact.nome, contact.responsavel, contact.telefone, stageLabel]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleOutsideClick = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    let cancelled = false;

    const loadTemplates = async () => {
      try {
        setLoading(true);
        const loadedTemplates = await fetchTemplatesByStage(stageKey);
        if (cancelled) return;

        const sortedTemplates = [...loadedTemplates].sort((left, right) => (left.sortOrder || 0) - (right.sortOrder || 0));
        setTemplates(sortedTemplates);

        const firstTemplate = sortedTemplates[0] || null;
        setSelectedTemplateId(firstTemplate?.id || '');
        setDraftMessage(
          firstTemplate ? applyWhatsAppTemplateVariables(firstTemplate.content, templateContext) : ''
        );
      } catch (error) {
        if (!cancelled) {
          console.error('Erro ao carregar templates do WhatsApp:', error);
          toast.error(error instanceof Error ? error.message : 'Erro ao carregar templates');
          setTemplates([]);
          setSelectedTemplateId('');
          setDraftMessage('');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadTemplates();

    return () => {
      cancelled = true;
    };
  }, [isOpen, stageKey, templateContext]);

  useEffect(() => {
    if (!selectedTemplate) {
      return;
    }

    setDraftMessage(applyWhatsAppTemplateVariables(selectedTemplate.content, templateContext));
  }, [selectedTemplate, templateContext]);

  const handleConfirm = () => {
    if (!validatePhone(contact.telefone)) {
      toast.error('Este contato não possui um número de WhatsApp cadastrado.');
      return;
    }

    const finalMessage = applyWhatsAppTemplateVariables(draftMessage.trim(), templateContext);
    const url = buildWhatsAppDeepLink(contact.telefone || '', finalMessage);

    if (!url) {
      toast.error('Este contato não possui um número de WhatsApp cadastrado.');
      return;
    }

    window.open(url, '_blank', 'noopener,noreferrer');
    if (selectedTemplate) {
      registerTemplateUsage(selectedTemplate.id, 'WHATSAPP').catch((error) => {
        console.error('Erro ao registrar uso do template:', error);
      });
    }
    onClose();
  };

  if (!isOpen) {
    return null;
  }

  return createPortal(
    <div
      ref={popoverRef}
      onClick={(event) => event.stopPropagation()}
      style={{
        position: 'fixed',
        left: `${popoverPosition.left}px`,
        top: `${popoverPosition.top}px`
      }}
      className="z-[9999] w-[360px] rounded-3xl border border-slate-200 bg-white p-4 shadow-[0_24px_60px_rgba(15,23,42,0.18)]"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-emerald-600">WhatsApp</p>
          <h4 className="mt-1 text-sm font-semibold text-slate-950">{contact.nome}</h4>
          <p className="text-xs text-slate-500">{stageLabel}</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-full border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-500 hover:bg-slate-50"
        >
          Fechar
        </button>
      </div>

      <div className="mt-4 space-y-2">
        <p className="text-xs font-medium text-slate-500">Templates desta etapa</p>
        {loading ? (
          <div className="rounded-2xl border border-dashed border-slate-200 px-4 py-6 text-center text-sm text-slate-500">
            Carregando templates...
          </div>
        ) : templates.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-amber-200 bg-amber-50 px-4 py-6 text-center text-sm text-amber-800">
            Nenhum template está disponível para esta etapa do funil.
          </div>
        ) : (
          <div className="max-h-40 space-y-2 overflow-y-auto pr-1">
            {templates.map((template) => (
              <button
                key={template.id}
                type="button"
                onClick={() => {
                  setSelectedTemplateId(template.id);
                  setDraftMessage(applyWhatsAppTemplateVariables(template.content, templateContext));
                }}
                className={`w-full rounded-2xl border px-3 py-3 text-left transition ${
                  selectedTemplateId === template.id
                    ? 'border-emerald-300 bg-emerald-50'
                    : 'border-slate-200 bg-slate-50 hover:border-slate-300 hover:bg-slate-100'
                }`}
              >
                <p className="text-sm font-semibold text-slate-900">{template.name}</p>
                {template.description ? <p className="mt-1 text-xs text-slate-500">{template.description}</p> : null}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="mt-4 space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-xs font-medium text-slate-500">Mensagem para enviar</p>
          <p className="text-[11px] text-slate-400">Editável antes de confirmar</p>
        </div>
        <textarea
          value={draftMessage}
          onChange={(event) => setDraftMessage(event.target.value)}
          rows={7}
          className="w-full rounded-2xl border border-slate-200 px-3 py-3 text-sm text-slate-700 outline-none focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100"
          placeholder="Selecione um template para carregar a mensagem..."
        />
      </div>

      <div className="mt-4 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-xs font-medium text-slate-500">{contact.telefone || 'Sem WhatsApp'}</p>
          <p className="text-[11px] text-slate-400">A mensagem será aberta no WhatsApp Web.</p>
        </div>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={!templates.length || !draftMessage.trim()}
          className="rounded-2xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Confirmar
        </button>
      </div>
    </div>,
    document.body
  );
}
